import React__default, { useMemo, Children, useRef, cloneElement } from 'react';
import styled from 'styled-components';
import { I as InputLabel } from '../InputLabel/InputLabel-Dnf2KXR4.js';
import { B as ButtonGroupContextProvider } from './ButtonGroupContext-Cv5DXHcj.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-CVa_vjg3-CVa_vjg3.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';

const FieldSet = /*#__PURE__*/styled.fieldset.withConfig({
  componentId: "sc-storm-ui-40027066__sc-w0bwkc-0"
})(["border:none;margin:0;padding:0;margin-block-start:unset;margin-block-end:unset;margin-inline-start:unset;margin-inline-end:unset;padding-inline-start:unset;"]);
const StyledButtonGroup = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40027066__sc-w0bwkc-1"
})(["background-clip:padding-box;border:1px solid ", ";width:fit-content;margin:0;:focus-within,:focus{outline:none;}margin-inline-start:0;margin-inline-end:0;min-inline-size:0;display:flex;padding:", ";align-items:center;gap:", ";background-color:", ";border-radius:", ";box-shadow:", ";"], ({
  theme
}) => theme.buttonGroup.borderColor, ({
  theme
}) => theme.spacing.xxsmall.default, ({
  theme
}) => theme.spacing[250], ({
  theme
}) => theme.surface.offset, ({
  theme
}) => theme.border.radius.round, ({
  theme
}) => theme.buttonGroup.innerShadow);
const ButtonGroup = ({
  onChange = noop,
  selectedValue = '',
  name,
  small = false,
  children,
  label = '',
  showLabel = false
}) => {
  const tabbableChildIndex = useMemo(() => {
    const childrenArray = Children.toArray(children);
    const selectedAndNotDisabledChildIndex = childrenArray.findIndex(child => child.props.value === selectedValue && !child.props.disabled);
    if (selectedAndNotDisabledChildIndex > -1) {
      return selectedAndNotDisabledChildIndex;
    }
    return childrenArray.findIndex(child => !child.props.disabled);
  }, [children, selectedValue]);
  const buttonGroupRef = useRef(null);

  // KeyDown events to handle for keyboard accessibility. Best practices for Tabs with manual
  // activation here: https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-2/tabs.html
  const handleKeyDown = event => {
    const {
      key
    } = event;

    // By default, buttons handle the Enter key on KeyDown. We prevent this because we want
    // to listen to KeyUp to prevent repeat triggers.
    if (key === KEYBOARD_KEYNAMES.ENTER) {
      event.preventDefault();
    }
    if (typeof document !== 'undefined' && buttonGroupRef.current) {
      const focusableEls = getFocusableElements(buttonGroupRef.current);
      const firstFocusableEl = focusableEls[0];
      const lastFocusableEl = focusableEls[focusableEls.length - 1];

      // When "Left Arrow" or "Right Arrow" is pressed, move focus to the left or right
      if (key === KEYBOARD_KEYNAMES.ARROW_DOWN || key === KEYBOARD_KEYNAMES.ARROW_UP || key === KEYBOARD_KEYNAMES.ARROW_LEFT || key === KEYBOARD_KEYNAMES.ARROW_RIGHT) {
        var _focusableEls$newFocu;
        const currentIndex = focusableEls.indexOf(document.activeElement);
        let newFocusIndex = 0;

        // When "Down Arrow" is pressed, focus to the next
        if (key === KEYBOARD_KEYNAMES.ARROW_DOWN || key === KEYBOARD_KEYNAMES.ARROW_RIGHT) {
          newFocusIndex = currentIndex + 1;
          if (document.activeElement === lastFocusableEl) {
            newFocusIndex = 0;
          }
        }

        // When "Up Arrow" is pressed, focus to the previous
        if (key === KEYBOARD_KEYNAMES.ARROW_UP || key === KEYBOARD_KEYNAMES.ARROW_LEFT) {
          newFocusIndex = currentIndex - 1;
          if (document.activeElement === firstFocusableEl) {
            newFocusIndex = focusableEls.length - 1;
          }
        }
        (_focusableEls$newFocu = focusableEls[newFocusIndex]) === null || _focusableEls$newFocu === void 0 || _focusableEls$newFocu.focus();
        event.preventDefault();
      }
    }
  };
  const handleChangeActive = (value, event) => {
    if (value !== selectedValue && onChange) {
      onChange(value, event);
    }
  };
  return /*#__PURE__*/React__default.createElement(FieldSet, null, label && /*#__PURE__*/React__default.createElement(InputLabel, {
    renderAs: "legend",
    hidden: !showLabel
  }, label), /*#__PURE__*/React__default.createElement(StyledButtonGroup, {
    role: "radiogroup",
    ref: buttonGroupRef,
    onKeyDown: event => handleKeyDown(event)
  }, /*#__PURE__*/React__default.createElement(ButtonGroupContextProvider, {
    name: name,
    small: small,
    selectedValue: selectedValue,
    onChange: handleChangeActive
  }, Children.map(children, (child, index) => (/*#__PURE__*/cloneElement(child, {
    tabIndex: index === tabbableChildIndex ? 0 : -1
  }))))));
};

export { ButtonGroup as B };
//# sourceMappingURL=ButtonGroup-KlbRJM_O.js.map
